import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  Users,
  Wrench,
  User,
  HelpCircle,
  Plus,
  TrendingUp,
  Target,
  Clock,
  X,
  Search,
  UserPlus,
  MessageSquare,
  AlertCircle
} from 'lucide-react';
import { BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Section, StatCard, Button, LoadingSpinner } from './components';

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartPeriod, setChartPeriod] = useState('week'); // 'week', 'month', 'quarter'

  // FAB menu and follow-up modal state
  const [fabMenuOpen, setFabMenuOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [personSearch, setPersonSearch] = useState('');
  const [personResults, setPersonResults] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [followUpWeeks, setFollowUpWeeks] = useState(4);
  const [followUpNote, setFollowUpNote] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [stats, setStats] = useState({
    todayAppointments: 0,
    newLeads: 0,
    activeTechnicians: 0,
    totalClients: 0,
    pendingRequests: 0
  });
  const [leadCounts, setLeadCounts] = useState(null);
  const [appointmentsChartData, setAppointmentsChartData] = useState([]);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [revenueTargets, setRevenueTargets] = useState([]);
  const [monthlyActuals, setMonthlyActuals] = useState({});
  const [curMonthBooked, setCurMonthBooked] = useState(0);

  useEffect(() => {
    const init = async () => {
      await fetchDashboardData();
      await fetchRevenueTargets();
    };
    init();
  }, []);

  // Fetch chart data when period changes
  useEffect(() => {
    fetchChartData(chartPeriod);
  }, [chartPeriod]);

  const fetchRevenueTargets = async () => {
    try {
      const data = await apiService.getTargets({ metric: 'revenue' });
      setRevenueTargets(data.results || data || []);
    } catch (err) {
      console.error('Error fetching targets:', err);
    }

    // Use monthly metrics endpoint for accurate revenue actuals
    try {
      const metricsData = await apiService.getMonthlyMetrics(13);
      const actuals = {};
      if (metricsData.data && Array.isArray(metricsData.data)) {
        metricsData.data.forEach(item => {
          const d = new Date(item.month);
          const key = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}`;
          actuals[key] = Math.round(item.revenue || 0);
        });
      }
      setMonthlyActuals(actuals);
    } catch (err) {
      console.error('Error fetching monthly actuals:', err);
    }

    // Fetch current month booked revenue from appointments
    try {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth() + 1;
      const appts = await apiService.getAppointments({ year: y, month: m, page_size: 500 });
      const results = (appts.results || appts || []).filter(a => a.status === 'ACCEPTED' || a.status === 'COMPLETED');
      const booked = results.reduce((sum, a) => sum + Math.max(parseFloat(a.expected_revenue || 0), parseFloat(a.total_payment || 0)), 0);
      setCurMonthBooked(Math.round(booked));
    } catch (err) {
      console.error('Error fetching booked revenue:', err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Use local date, not UTC (toISOString would give wrong date after ~7pm EST)
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const [
        todayAppts,
        allClients,
        allTechnicians,
        allInteractions,
        cmdCenter
      ] = await Promise.all([
        apiService.getAppointments({ date_time__date: today }),
        apiService.getClients(),
        apiService.getStaff({ is_technician: true }),
        apiService.getInteractions(),
        apiService.get('/amn/leads/command-center/').catch(() => null)
      ]);

      if (cmdCenter) setLeadCounts(cmdCenter);

      // Show active appointments (exclude cancelled)
      const acceptedAppts = (todayAppts.results || []).filter(
        appt => !appt.status?.toUpperCase().includes('CANCEL')
      );

      setStats({
        todayAppointments: acceptedAppts.length,
        newLeads: allInteractions.results?.length || 0,
        activeTechnicians: allTechnicians.results?.filter(t => t.internal_status === 'active').length || 0,
        totalClients: allClients.count || 0,
        pendingRequests: allInteractions.results?.filter(i => i.status === 'pending').length || 0
      });

      setTodayAppointments(acceptedAppts);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async (period) => {
    try {
      setChartLoading(true);
      const appointments = [];
      const revenue = [];

      if (period === 'week') {
        // Weekly data - last 52 weeks
        const metricsData = await apiService.getWeeklyMetrics(52);
        if (metricsData.data && Array.isArray(metricsData.data)) {
          metricsData.data.forEach(week => {
            const weekDate = new Date(week.week);
            const weekLabel = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
            appointments.push({ date: weekLabel, count: week.appointments || 0 });
            revenue.push({ date: weekLabel, amount: Math.round(week.revenue || 0) });
          });
        }
      } else if (period === 'month') {
        // Monthly data - last 24 months
        const metricsData = await apiService.getMonthlyMetrics(24);
        if (metricsData.data && Array.isArray(metricsData.data)) {
          metricsData.data.forEach(item => {
            const monthDate = new Date(item.month);
            const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
            appointments.push({ date: monthLabel, count: item.appointments || 0 });
            revenue.push({ date: monthLabel, amount: Math.round(item.revenue || 0) });
          });
        }
      } else if (period === 'quarter') {
        // Quarterly data - last 10 quarters
        const metricsData = await apiService.getQuarterlyMetrics(10);
        if (metricsData.data && Array.isArray(metricsData.data)) {
          metricsData.data.forEach(item => {
            const quarterLabel = item.quarter_label || 'Q?';
            appointments.push({ date: quarterLabel, count: item.appointments || 0 });
            revenue.push({ date: quarterLabel, amount: Math.round(item.revenue || 0) });
          });
        }
      }

      setAppointmentsChartData(appointments);
      setRevenueChartData(revenue);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setAppointmentsChartData([]);
      setRevenueChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  // Helper to get ISO week number
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Merge targets into revenue data at render time
  const revenueChartDataWithTargets = useMemo(() => {
    if (chartPeriod !== 'month' || revenueTargets.length === 0) return revenueChartData;
    const targetMap = {};
    revenueTargets.forEach(t => {
      const label = new Date(t.year, t.month - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      targetMap[label] = Math.round(parseFloat(t.target_value));
    });
    return revenueChartData.map(r => ({
      ...r,
      target: targetMap[r.date] !== undefined ? targetMap[r.date] : undefined
    }));
  }, [revenueChartData, revenueTargets, chartPeriod]);

  // Revenue target summary for current & previous month
  const targetSummary = useMemo(() => {
    if (revenueTargets.length === 0 || Object.keys(monthlyActuals).length === 0) return null;

    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth() + 1;
    const prevMonth = curMonth === 1 ? 12 : curMonth - 1;
    const prevYear = curMonth === 1 ? curYear - 1 : curYear;

    const curTarget = revenueTargets.find(t => t.year === curYear && t.month === curMonth);
    const prevTarget = revenueTargets.find(t => t.year === prevYear && t.month === prevMonth);

    const curActual = Math.round(monthlyActuals[`${curYear}-${curMonth}`] || 0);
    const prevActual = Math.round(monthlyActuals[`${prevYear}-${prevMonth}`] || 0);
    const curTargetVal = curTarget ? Math.round(parseFloat(curTarget.target_value)) : 0;
    const prevTargetVal = prevTarget ? Math.round(parseFloat(prevTarget.target_value)) : 0;

    // Yearly totals
    const yearTargets = revenueTargets.filter(t => t.year === curYear);
    const yearlyTarget = yearTargets.reduce((s, t) => s + parseFloat(t.target_value), 0);

    // YTD actual and YTD target (sum of monthly targets through current month)
    let ytdActual = 0;
    let ytdTarget = 0;
    for (let m = 1; m <= curMonth; m++) {
      ytdActual += Math.round(monthlyActuals[`${curYear}-${m}`] || 0);
      const mt = revenueTargets.find(t => t.year === curYear && t.month === m);
      if (mt) ytdTarget += parseFloat(mt.target_value);
    }

    const dayOfMonth = now.getDate();
    const daysInMonth = new Date(curYear, curMonth, 0).getDate();
    const monthProgress = Math.round((dayOfMonth / daysInMonth) * 100);

    // Expected revenue so far: full target for completed months + prorated current month
    let ytdExpected = 0;
    for (let m = 1; m < curMonth; m++) {
      const mt = revenueTargets.find(t => t.year === curYear && t.month === m);
      if (mt) ytdExpected += parseFloat(mt.target_value);
    }
    ytdExpected += curTargetVal * (dayOfMonth / daysInMonth);
    ytdExpected = Math.round(ytdExpected);

    return {
      curActual, curTargetVal, curMonth, curYear, monthProgress,
      prevActual, prevTargetVal, prevMonth, prevYear,
      yearlyTarget, ytdActual, ytdTarget, ytdExpected,
    };
  }, [revenueTargets, monthlyActuals]);

  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  // Group appointments by technician
  const groupAppointmentsByTechnician = (appointments) => {
    const grouped = {};

    appointments.forEach(appt => {
      const techName = appt.technician_name || 'Unassigned';
      if (!grouped[techName]) {
        grouped[techName] = [];
      }
      grouped[techName].push(appt);
    });

    // Sort appointments within each technician by time
    Object.keys(grouped).forEach(techName => {
      grouped[techName].sort((a, b) => {
        const timeA = new Date(a.date_time).getTime();
        const timeB = new Date(b.date_time).getTime();
        return timeA - timeB;
      });
    });

    return grouped;
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Search for persons/clients
  const handlePersonSearch = async (query) => {
    setPersonSearch(query);
    if (query.length < 2) {
      setPersonResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      const data = await apiService.get('/amn/persons/', { search: query, page_size: 10 });
      setPersonResults(data.results || []);
    } catch (err) {
      console.error('Error searching persons:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Create follow-up reminder
  const handleCreateFollowUp = async () => {
    if (!selectedPerson) return;
    try {
      setSubmitting(true);
      const followUpDate = new Date();
      followUpDate.setDate(followUpDate.getDate() + (followUpWeeks * 7));
      const formattedDate = followUpDate.toISOString().split('T')[0];

      await apiService.post('/amn/interactions/', {
        person: selectedPerson.id,
        date_time: new Date().toISOString(),
        type: 'follow_up_reminder',
        content: followUpNote || `Follow-up reminder scheduled for ${followUpWeeks} weeks`,
        status: 'snoozed',
        snooze_until: formattedDate,
        snooze_comment: followUpNote || `Scheduled follow-up in ${followUpWeeks} weeks`,
      });

      // Reset and close
      setFollowUpModalOpen(false);
      setSelectedPerson(null);
      setPersonSearch('');
      setPersonResults([]);
      setFollowUpWeeks(4);
      setFollowUpNote('');
      setFabMenuOpen(false);

      // Refresh dashboard data
      fetchDashboardData();
    } catch (err) {
      console.error('Error creating follow-up:', err);
      alert('Failed to create follow-up. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Compact Header */}
        <div className="mb-3">
          <h1 className="text-lg font-semibold text-gray-900 leading-none mb-0.5">Dashboard</h1>
          <p className="text-xs text-gray-500">{getCurrentDate()}</p>
        </div>

        {/* Loading State */}
        {loading && (
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        )}

        {/* Dashboard Content */}
        {!loading && (
          <>
            {/* Road to $1M Valuation */}
            {targetSummary && (
              <Panel className="mb-3 border-l-4 border-amber-500">
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-amber-600" />
                    <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Road to $1M Valuation — End of 2027</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {/* Previous Month */}
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="text-xs text-gray-500">
                        {new Date(targetSummary.prevYear, targetSummary.prevMonth - 1).toLocaleDateString('en-US', { month: 'long' })} Result
                      </p>
                      <p className="text-base font-bold text-gray-900">${targetSummary.prevActual.toLocaleString()}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Target className="w-3 h-3 text-amber-500" />
                        <span className="text-xs text-gray-500">${targetSummary.prevTargetVal.toLocaleString()} target</span>
                      </div>
                      {targetSummary.prevTargetVal > 0 && (
                        <p className={`text-xs font-semibold mt-0.5 ${targetSummary.prevActual >= targetSummary.prevTargetVal ? 'text-green-600' : 'text-red-600'}`}>
                          {targetSummary.prevActual >= targetSummary.prevTargetVal ? '✓' : '✗'} {Math.round((targetSummary.prevActual / targetSummary.prevTargetVal) * 100)}% of target
                        </p>
                      )}
                    </div>

                    {/* Current Month */}
                    <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                      <p className="text-xs text-amber-700 font-medium">
                        {new Date(targetSummary.curYear, targetSummary.curMonth - 1).toLocaleDateString('en-US', { month: 'long' })} (in progress)
                      </p>
                      <p className="text-base font-bold text-green-700">${targetSummary.curActual.toLocaleString()}</p>
                      <p className="text-[10px] text-gray-400 -mt-0.5">collected</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Target className="w-3 h-3 text-amber-500" />
                        <span className="text-xs text-gray-500">${targetSummary.curTargetVal.toLocaleString()} target</span>
                      </div>
                      {curMonthBooked > 0 && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Calendar className="w-3 h-3 text-blue-500" />
                          <span className="text-xs text-blue-600 font-medium">${curMonthBooked.toLocaleString()} booked</span>
                          {curMonthBooked > targetSummary.curActual && (
                            <span className="text-xs text-amber-600">(${(curMonthBooked - targetSummary.curActual).toLocaleString()} pending)</span>
                          )}
                        </div>
                      )}
                      {/* Progress bar */}
                      <div className="mt-1">
                        <div className="flex justify-between text-xs text-gray-400 mb-0.5">
                          <span>{targetSummary.monthProgress}% of month</span>
                          <span>{targetSummary.curTargetVal > 0 ? Math.round((targetSummary.curActual / targetSummary.curTargetVal) * 100) : 0}% of target</span>
                        </div>
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${targetSummary.curTargetVal > 0 && (targetSummary.curActual / targetSummary.curTargetVal) >= (targetSummary.monthProgress / 100) ? 'bg-green-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(100, targetSummary.curTargetVal > 0 ? (targetSummary.curActual / targetSummary.curTargetVal) * 100 : 0)}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* YTD */}
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="text-xs text-gray-500">{targetSummary.curYear} YTD</p>
                      <p className="text-base font-bold text-gray-900">${targetSummary.ytdActual.toLocaleString()}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Target className="w-3 h-3 text-amber-500" />
                        <span className="text-xs text-gray-500">${Math.round(targetSummary.ytdTarget).toLocaleString()} target</span>
                      </div>
                      {targetSummary.ytdExpected > 0 && (() => {
                        const pctOfExpected = Math.round((targetSummary.ytdActual / targetSummary.ytdExpected) * 1000) / 10;
                        const diff = targetSummary.ytdActual - targetSummary.ytdExpected;
                        const ahead = diff >= 0;
                        return (
                          <>
                            <p className={`text-xs font-semibold mt-0.5 ${ahead ? 'text-green-600' : 'text-red-600'}`}>
                              {pctOfExpected}% of pace · {ahead ? '+' : '-'}${Math.abs(diff).toLocaleString()} {ahead ? 'ahead' : 'behind'}
                            </p>
                          </>
                        );
                      })()}
                    </div>

                    {/* Valuation Projection */}
                    {(() => {
                      const target2027 = revenueTargets.filter(t => t.year === 2027).reduce((s, t) => s + parseFloat(t.target_value), 0);
                      return (
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-xs text-gray-500">$1M Valuation Goal</p>
                          <p className="text-base font-bold text-gray-900">Dec 2027</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            2027 run rate: ~${Math.round(target2027 / 1000)}k annualized
                          </p>
                          <p className="text-xs text-amber-600 font-medium mt-0.5">
                            {targetSummary.yearlyTarget > 0 ? `${targetSummary.curYear} target: $${Math.round(targetSummary.yearlyTarget / 1000)}k` : ''}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </Panel>
            )}

            {/* Chart Period Toggle */}
            <div className="flex items-center justify-end gap-1 mb-2">
              <span className="text-xs text-gray-500 mr-1">View by:</span>
              {[
                { key: 'week', label: 'Week', count: '52W' },
                { key: 'month', label: 'Month', count: '24M' },
                { key: 'quarter', label: 'Quarter', count: '10Q' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setChartPeriod(key)}
                  className={`px-2 py-0.5 text-xs rounded transition-colors ${
                    chartPeriod === key
                      ? 'bg-red-800 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Charts - Compact Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
              {/* Appointments Chart */}
              <Panel>
                <Section title={`Appointments (${chartPeriod === 'week' ? '52W' : chartPeriod === 'month' ? '24M' : '10Q'})`} tight>
                  {chartLoading ? (
                    <div className="h-[140px] flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-800"></div>
                    </div>
                  ) : appointmentsChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={appointmentsChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis
                          dataKey="date"
                          stroke="#9ca3af"
                          fontSize={9}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="#9ca3af"
                          fontSize={9}
                          tickLine={false}
                          axisLine={false}
                          width={25}
                        />
                        <Tooltip
                          contentStyle={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '11px'
                          }}
                        />
                        <Bar dataKey="count" fill="#dc2626" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">
                      No data
                    </div>
                  )}
                </Section>
              </Panel>

              {/* Revenue Chart */}
              <Panel>
                <Section title={`Revenue (${chartPeriod === 'week' ? '52W' : chartPeriod === 'month' ? '24M' : '10Q'})`} tight>
                  {chartLoading ? (
                    <div className="h-[140px] flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-800"></div>
                    </div>
                  ) : revenueChartDataWithTargets.length > 0 ? (
                    <ResponsiveContainer width="100%" height={140}>
                      <ComposedChart data={revenueChartDataWithTargets}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis
                          dataKey="date"
                          stroke="#9ca3af"
                          fontSize={9}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="#9ca3af"
                          fontSize={9}
                          tickLine={false}
                          axisLine={false}
                          width={30}
                        />
                        <Tooltip
                          formatter={(value, name) => [`$${value.toLocaleString()}`, name === 'target' ? 'Target' : 'Revenue']}
                          contentStyle={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '11px'
                          }}
                        />
                        <Bar dataKey="amount" fill="#dc2626" radius={[3, 3, 0, 0]} name="Revenue" />
                        {chartPeriod === 'month' && revenueChartDataWithTargets.some(d => d.target) && (
                          <Line type="monotone" dataKey="target" stroke="#f59e0b" strokeWidth={2} dot={false} name="Target" />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">
                      No data
                    </div>
                  )}
                </Section>
              </Panel>
            </div>

            {/* Lead Action Center */}
            {leadCounts && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div
                  className="bg-blue-50 border border-blue-200 rounded-lg p-3 cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => navigate('/leads?status=new')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <UserPlus className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-600 uppercase">New Leads</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{leadCounts.new_leads || 0}</p>
                </div>
                <div
                  className="bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => navigate('/leads?status=follow_up')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-600 uppercase">Leads Follow Up</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-900">{leadCounts.follow_up || 0}</p>
                  {leadCounts.overdue > 0 && (
                    <p className="text-xs text-red-600 mt-0.5">{leadCounts.overdue} overdue</p>
                  )}
                </div>
              </div>
            )}

            {/* Today's Schedule - By Technician Columns */}
            <Panel noPadding>
              <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Today's Schedule ({todayAppointments.length} appointments)</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/appointments')}
                >
                  View All
                </Button>
              </div>

              <div className="p-2">
                {todayAppointments.length === 0 ? (
                  <div className="text-center py-4 text-xs text-gray-500">
                    No appointments today
                  </div>
                ) : (
                  <div className="flex gap-2 overflow-x-auto">
                    {Object.entries(groupAppointmentsByTechnician(todayAppointments)).map(([techName, appointments]) => (
                      <div
                        key={techName}
                        className="flex-shrink-0 w-64 bg-white border border-gray-200 rounded-lg overflow-hidden"
                      >
                        {/* Technician Header */}
                        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            {appointments[0]?.technician ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/technicians/${appointments[0].technician}`);
                                }}
                                className="text-xs font-semibold text-red-700 hover:text-red-900 hover:underline transition-colors"
                              >
                                {techName}
                              </button>
                            ) : (
                              <h4 className="text-xs font-semibold text-gray-900">{techName}</h4>
                            )}
                            <span className="text-xs text-gray-500">{appointments.length} appts</span>
                          </div>
                        </div>

                        {/* Appointments List */}
                        <div className="p-2 space-y-2 max-h-[500px] overflow-y-auto">
                          {appointments.map((appt) => (
                            <div
                              key={appt.id}
                              onClick={() => navigate(`/appointments/${appt.id}`)}
                              className={`border rounded p-2 hover:border-red-800 hover:shadow-sm cursor-pointer transition-all ${
                                (appt.internal_status === 'completed' || appt.status?.toUpperCase() === 'COMPLETED')
                                  ? 'bg-green-50/70 border-green-300'
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-1">
                                <span className="text-xs font-semibold text-red-800">
                                  {formatTime(appt.date_time)}
                                </span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  appt.is_paid
                                    ? 'bg-green-50 text-green-700'
                                    : 'bg-gray-50 text-gray-600'
                                }`}>
                                  {appt.status || 'N/A'}
                                </span>
                              </div>
                              <div className="text-xs font-medium mb-1">
                                {appt.client ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/clients/${appt.client}`);
                                    }}
                                    className="text-red-700 hover:text-red-900 hover:underline transition-colors"
                                  >
                                    {appt.client_name || 'Unknown Client'}
                                  </button>
                                ) : (
                                  <span className="text-gray-900">{appt.client_name || 'Unknown Client'}</span>
                                )}
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>
                                  ${appt.total_payment || appt.expected_revenue || '0'}
                                </span>
                                {appt.is_alf && (
                                  <span className="text-xs text-purple-600 font-medium">ALF</span>
                                )}
                                {appt.is_event && (
                                  <span className="text-xs text-blue-600 font-medium">Event</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Panel>
          </>
        )}
      </div>

      {/* FAB Menu */}
      {fabMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setFabMenuOpen(false)}
        />
      )}
      <div className="fixed bottom-3 right-3 z-50">
        {/* Menu Options */}
        {fabMenuOpen && (
          <div className="absolute bottom-12 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden min-w-[180px]">
            <button
              onClick={() => {
                setFabMenuOpen(false);
                navigate('/appointments');
              }}
              className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100"
            >
              <Calendar className="w-4 h-4 text-gray-500" />
              New Appointment
            </button>
            <button
              onClick={() => {
                setFabMenuOpen(false);
                setFollowUpModalOpen(true);
              }}
              className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <Clock className="w-4 h-4 text-gray-500" />
              Schedule Follow-Up
            </button>
          </div>
        )}

        {/* FAB Button */}
        <button
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all border ${
            fabMenuOpen
              ? 'bg-gray-600 border-gray-700 rotate-45'
              : 'bg-red-800 border-red-900 hover:bg-red-900'
          }`}
          onClick={() => setFabMenuOpen(!fabMenuOpen)}
          title="Quick Actions"
        >
          <Plus className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Schedule Follow-Up Modal */}
      {followUpModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Follow-Up</h3>
              <button
                onClick={() => {
                  setFollowUpModalOpen(false);
                  setSelectedPerson(null);
                  setPersonSearch('');
                  setPersonResults([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Person Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client / Person
                </label>
                {selectedPerson ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedPerson.first_name} {selectedPerson.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{selectedPerson.phone_number}</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedPerson(null);
                        setPersonSearch('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name or phone..."
                        value={personSearch}
                        onChange={(e) => handlePersonSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent"
                      />
                    </div>
                    {/* Search Results Dropdown */}
                    {personResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {personResults.map((person) => (
                          <button
                            key={person.id}
                            onClick={() => {
                              setSelectedPerson(person);
                              setPersonResults([]);
                              setPersonSearch('');
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                          >
                            <p className="text-sm font-medium text-gray-900">
                              {person.first_name} {person.last_name}
                            </p>
                            <p className="text-xs text-gray-500">{person.phone_number}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchLoading && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center text-sm text-gray-500">
                        Searching...
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Follow-up Timing */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Follow up in
                </label>
                <select
                  value={followUpWeeks}
                  onChange={(e) => setFollowUpWeeks(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent"
                >
                  <option value={2}>2 weeks</option>
                  <option value={4}>4 weeks (default)</option>
                  <option value={6}>6 weeks</option>
                  <option value={8}>8 weeks</option>
                </select>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optional)
                </label>
                <textarea
                  value={followUpNote}
                  onChange={(e) => setFollowUpNote(e.target.value)}
                  placeholder="e.g., Discussed gel nails, interested in trying..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setFollowUpModalOpen(false);
                  setSelectedPerson(null);
                  setPersonSearch('');
                  setPersonResults([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFollowUp}
                disabled={!selectedPerson || submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-800 hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Creating...' : 'Schedule Follow-Up'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
